/**
 * Locale registry for tamildata.org
 * ---------------------------------
 * The language list is driven by where Tamil communities actually live, not by
 * global speaker counts. Each entry records the diaspora region it serves so
 * the reasoning stays visible to whoever edits this next.
 *
 * Adding a language:
 *   1. Add an entry to LOCALES below.
 *   2. Create src/i18n/messages/<code>.js.
 *   3. Add a `lang.region.<code>` string to every existing catalogue.
 *   4. Run `npm run i18n:coverage` to see which keys still need translating.
 *
 * Untranslated keys fall back to English automatically, so a partial
 * catalogue is safe to ship.
 *
 * Note: every current locale is left-to-right. If an RTL language (Arabic,
 * Hebrew, Urdu, Persian) is ever added, set `dir: "rtl"` on its entry and
 * restore the RTL handling described in the i18n section of
 * .kiro/steering/general-guidelines.md.
 */

export const DEFAULT_LOCALE = "en";
export const FALLBACK_LOCALE = "en";

/**
 * Groups used by the language switcher, in display order.
 * `primary` is pinned to the top of the dropdown.
 */
export const LOCALE_GROUPS = [
  { id: "primary", labelKey: "lang.group.primary" },
  { id: "europe", labelKey: "lang.group.europe" },
];

export const LOCALES = [
  // -- Primary -------------------------------------------------------------
  {
    code: "en",
    // `nativeName` is intentionally never translated: a speaker looking for
    // their language needs to recognise it without already understanding
    // the current interface language.
    nativeName: "English",
    englishName: "English",
    dir: "ltr",
    group: "primary",
    // Shown as a subtitle in the switcher, translated via `lang.region.<code>`.
    regionKey: "lang.region.en",
    htmlLang: "en",
  },
  {
    code: "ta",
    nativeName: "தமிழ்",
    englishName: "Tamil",
    dir: "ltr",
    group: "primary",
    regionKey: "lang.region.ta",
    htmlLang: "ta",
  },

  // -- Europe --------------------------------------------------------------
  {
    code: "de",
    nativeName: "Deutsch",
    englishName: "German",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.de",
    htmlLang: "de",
  },
  {
    code: "fr",
    nativeName: "Français",
    englishName: "French",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.fr",
    htmlLang: "fr",
  },
  {
    code: "es",
    nativeName: "Español",
    englishName: "Spanish",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.es",
    htmlLang: "es",
  },
  {
    code: "it",
    nativeName: "Italiano",
    englishName: "Italian",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.it",
    htmlLang: "it",
  },
  {
    code: "nl",
    nativeName: "Nederlands",
    englishName: "Dutch",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.nl",
    htmlLang: "nl",
  },
  {
    code: "no",
    nativeName: "Norsk",
    englishName: "Norwegian",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.no",
    htmlLang: "no",
  },
  {
    code: "da",
    nativeName: "Dansk",
    englishName: "Danish",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.da",
    htmlLang: "da",
  },
  {
    code: "sv",
    nativeName: "Svenska",
    englishName: "Swedish",
    dir: "ltr",
    group: "europe",
    regionKey: "lang.region.sv",
    htmlLang: "sv",
  },
];

export const LOCALE_CODES = LOCALES.map((l) => l.code);

const BY_CODE = LOCALES.reduce((acc, locale) => {
  acc[locale.code] = locale;
  return acc;
}, {});

export function getLocale(code) {
  return BY_CODE[code] || BY_CODE[DEFAULT_LOCALE];
}

export function isSupported(code) {
  return Object.prototype.hasOwnProperty.call(BY_CODE, code);
}

/**
 * Maps a browser language tag onto a supported locale.
 * Handles regional tags, so "de-CH" resolves to "de" and "fr-CA" to "fr".
 */
export function resolveBrowserLocale(languages = []) {
  const tags = Array.isArray(languages) ? languages : [languages];

  for (const raw of tags) {
    if (!raw) continue;
    const tag = String(raw).toLowerCase();

    if (isSupported(tag)) return tag;

    const base = tag.split("-")[0];
    if (isSupported(base)) return base;

    // Norwegian variants both map onto our single "no" catalogue.
    if (base === "nb" || base === "nn") return "no";
  }

  return null;
}

/**
 * Locales grouped for the switcher, skipping any group that ended up empty.
 */
export function groupedLocales() {
  return LOCALE_GROUPS.map((group) => ({
    ...group,
    locales: LOCALES.filter((locale) => locale.group === group.id),
  })).filter((group) => group.locales.length > 0);
}
